import { createTheme, type Theme } from '@mui/material/styles'
import { color, font, scale, surface, radius, motion, space, type Step } from './tokens'

/**
 * Tema MUI derivado de tokens.ts — o segundo emissor permitido, e só porque lê a MESMA fonte (ADR-001/009/013).
 *  - cssVariables com prefixo 'z' e seletor data-surface: o MUI passa a ler var(--z-*) — SCSS e MUI concordam por construção.
 *  - colorSchemes: light = papel, dark = tinta. A superfície é decidida por dobra (PageShell emite data-surface), nunca pelo sistema.
 *  - shadows todas 'none' (a marca é plana); sem gradiente; raio pela razão (ADR-002) nos overrides.
 *  - Estados sem alpha (ADR-013): disabled/hover/focus com pares declarados na matriz de contraste.
 */
const step = (k: keyof typeof scale) => {
  const s: Step = scale[k]
  return {
    fontFamily: font[s.voice],
    fontWeight: s.weight,
    fontSize: `${s.size}px`,
    lineHeight: s.lineHeight,
    letterSpacing: `${s.tracking}px`,
    ...(s.uppercase ? { textTransform: 'uppercase' as const } : {}),
  }
}

export const theme = createTheme({
  cssVariables: { cssVarPrefix: 'z', colorSchemeSelector: 'data-surface', disableCssColorScheme: true },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: { main: surface.paper.action, contrastText: color.papel },
        secondary: { main: surface.paper.fgMuted, contrastText: color.papel },
        success: { main: surface.paper.curaText, contrastText: color.papel },
        warning: { main: surface.paper.gateText, contrastText: color.papel },
        error: { main: surface.paper.falhaText, contrastText: color.papel },
        info: { main: color.conexao, contrastText: color.tinta },
        background: { default: surface.paper.bg, paper: surface.paper.bg },
        text: { primary: surface.paper.fg, secondary: surface.paper.fgMuted, disabled: surface.paper.fgMuted },
        divider: surface.paper.divider,
        action: { disabledBackground: surface.paper.chip, disabled: surface.paper.fgMuted, hover: 'transparent', focus: 'transparent', selected: 'transparent', active: surface.paper.action },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: { main: surface.ink.action, contrastText: color.tinta },
        secondary: { main: surface.ink.fgMuted, contrastText: color.tinta },
        success: { main: surface.ink.curaText, contrastText: color.tinta },
        warning: { main: surface.ink.gateText, contrastText: color.tinta },
        error: { main: surface.ink.falhaText, contrastText: color.tinta },
        info: { main: surface.ink.conexaoText, contrastText: color.tinta },
        background: { default: surface.ink.bg, paper: surface.ink.bg },
        text: { primary: surface.ink.fg, secondary: surface.ink.fgMuted, disabled: surface.ink.fgMuted },
        divider: surface.ink.divider,
        action: { disabledBackground: surface.ink.chip, disabled: surface.ink.fgMuted, hover: 'transparent', focus: 'transparent', selected: 'transparent', active: surface.ink.action },
      },
    },
  },
  // D25 (Jean, 2026-09-06, modelo do Ângelo): o chão volta a ser TINTA — herói em tinta, seções alternam tinta/papel
  // (exibição de site do próprio manual). <html data-surface="dark">, theme-color tinta. (D23 valeu entre 04 e 06/09.)
  defaultColorScheme: 'dark',
  spacing: (factor: number) => `${space[Math.min(space.length - 1, Math.max(0, Math.round(factor)))] ?? factor * 8}px`,
  shape: { borderRadius: 0 }, // o raio é razão do elemento, aplicado nos componentes (ADR-002) — nunca um número global
  shadows: Array(25).fill('none') as unknown as Theme['shadows'],
  typography: {
    fontFamily: font.sans,
    htmlFontSize: 16,
    h1: step('display'), // Display Newsreader Light 44 — o <h1> visual das páginas
    h2: step('h1'), // H1 da escala (Schibsted SemiBold 32) — título de seção
    h3: step('h2'), // H2 da escala (Schibsted Medium 22)
    h4: step('h2'),
    h5: step('corpo'),
    h6: step('corpoCompacto'),
    subtitle1: step('corpo'),
    subtitle2: step('corpoCompacto'),
    body1: step('corpo'),
    body2: step('corpoCompacto'),
    caption: step('dado'),
    overline: step('etiqueta'),
    button: { fontFamily: font.sans, fontWeight: 500, fontSize: `${scale.corpoCompacto.size}px`, lineHeight: 1.2, letterSpacing: 0, textTransform: 'none' },
  },
  transitions: {
    easing: { easeInOut: motion.easeArcoOut, easeOut: motion.easeArcoOut, easeIn: motion.easeArcoIn, sharp: motion.easeArcoOut },
    duration: { shortest: motion.durFastMs, shorter: motion.durFastMs, short: motion.durFastMs, standard: motion.durBaseMs, complex: motion.durBaseMs, enteringScreen: motion.durBaseMs, leavingScreen: motion.durBaseMs },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { fontSynthesis: 'none', textRendering: 'optimizeLegibility' },
        body: { fontFamily: font.sans, fontSize: `${scale.corpo.size}px`, lineHeight: scale.corpo.lineHeight },
        '*:focus-visible': { outline: `2px solid var(--z-action)`, outlineOffset: '3px', boxShadow: `0 0 0 1px var(--z-bg)`, transition: `outline-offset ${motion.durFastMs}ms ${motion.easeArcoOut}` },
      },
    },
    MuiButtonBase: { defaultProps: { disableRipple: true, disableTouchRipple: true } },
    MuiButton: {
      // variant fica no padrão do MUI ('text'): forçar 'contained' aqui mudou o fundo de botões antigos com cor cravada
      // e criou um par 1,29:1 em /products/autonomy-suite — a catraca de a11y pegou (docs/GATES.md).
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          // altura 44 → raio 4,4px (10% do menor lado, ADR-002); hover é geométrico (outline-offset), nunca cor nova
          minHeight: 44, padding: '0 20px', borderRadius: `calc(44px * ${radius.ratio})`, fontWeight: 500, textTransform: 'none', boxShadow: 'none',
          transition: `outline-offset ${motion.durFastMs}ms ${motion.easeArcoOut}`,
          '&:hover': { boxShadow: 'none', outline: '2px solid var(--z-action)', outlineOffset: '3px' },
          '&.Mui-disabled': { backgroundColor: 'var(--z-chip)', color: 'var(--z-fg-muted)', borderColor: 'var(--z-chip)' },
        },
        // chaves por COR (…Primary), não por variante: sobrescrever `text` genérico pintava color="inherit" de violeta
        // sobre o AppBar azul do Header antigo → 1,42:1 (a catraca de a11y pegou).
        containedPrimary: { backgroundColor: 'var(--z-action)', color: 'var(--z-bg)', '&:hover': { backgroundColor: 'var(--z-action)' } },
        outlinedPrimary: { borderWidth: 2, borderColor: 'var(--z-action)', color: 'var(--z-action)', '&:hover': { borderWidth: 2, borderColor: 'var(--z-action)', backgroundColor: 'transparent' } },
        textPrimary: { color: 'var(--z-action)', '&:hover': { backgroundColor: 'transparent' } },
        text: { '&:hover': { backgroundColor: 'transparent' } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: `calc(40px * ${radius.ratio})`, '&:hover': { backgroundColor: 'transparent', outline: '2px solid var(--z-action)', outlineOffset: '3px' } } } },
    MuiPaper: { defaultProps: { elevation: 0, square: true }, styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none', backgroundColor: 'var(--z-bg)', color: 'var(--z-fg)' } } },
    MuiCard: { defaultProps: { elevation: 0 }, styleOverrides: { root: { borderRadius: `${radius.framePx}px`, border: '1px solid var(--z-divider)', boxShadow: 'none', backgroundImage: 'none' } } },
    // sem color:'transparent' no defaultProps: isso trocava o texto do Header antigo (inherit) para tinta sobre azul → 3,8:1
    MuiAppBar: { defaultProps: { elevation: 0 }, styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none', backdropFilter: 'none' } } },
    MuiChip: {
      styleOverrides: {
        // chip 24px → raio 2,4px em dois cantos do MESMO lado ("aba"), nunca 50% nos quatro (ADR-002)
        root: { height: 24, borderRadius: `0 calc(24px * ${radius.ratio}) calc(24px * ${radius.ratio}) 0`, fontFamily: font.mono, fontSize: `${scale.etiqueta.size}px`, letterSpacing: `${scale.etiqueta.tracking}px`, textTransform: 'uppercase', fontWeight: 500 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: `calc(48px * ${radius.ratio})`,
          backgroundColor: 'transparent',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--z-line)', borderWidth: 1 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--z-line)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--z-action)', borderWidth: 2 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--z-falha-text)' },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--z-chip)' },
        },
        input: { color: 'var(--z-fg)', '&::placeholder': { color: 'var(--z-fg-muted)', opacity: 1 } },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: 'var(--z-fg-muted)', '&.Mui-focused': { color: 'var(--z-action)' } } } },
    MuiFormHelperText: { styleOverrides: { root: { fontFamily: font.mono, fontSize: `${scale.dado.size}px`, '&.Mui-error': { color: 'var(--z-falha-text)' } } } },
    MuiLink: { defaultProps: { underline: 'hover' }, styleOverrides: { root: { color: 'var(--z-action)', textDecorationColor: 'var(--z-action)' } } },
    MuiDivider: { styleOverrides: { root: { borderColor: 'var(--z-divider)' } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: 'var(--z-fg)', color: 'var(--z-bg)', borderRadius: `calc(32px * ${radius.ratio})`, fontFamily: font.mono, fontSize: `${scale.dado.size}px` } } },
    MuiAccordion: { defaultProps: { elevation: 0, square: true, disableGutters: true }, styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none', border: '1px solid var(--z-divider)', '&::before': { display: 'none' } } } },
    MuiDrawer: { styleOverrides: { paper: { backgroundImage: 'none', boxShadow: 'none', borderLeft: '1px solid var(--z-line)' } } },
    MuiMenu: { styleOverrides: { paper: { backgroundImage: 'none', boxShadow: 'none', border: '1px solid var(--z-line)', borderRadius: `calc(48px * ${radius.ratio})` } } },
    MuiMenuItem: { styleOverrides: { root: { '&:hover': { backgroundColor: 'transparent', outline: '2px solid var(--z-action)', outlineOffset: '-2px' }, '&.Mui-selected': { backgroundColor: 'transparent', color: 'var(--z-action)' } } } },
  },
})

export default theme
